import React, { useEffect } from 'react';
import { Button, DatePicker, Drawer, Form, InputNumber, message } from 'antd';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { RRPService } from 'services/RRPService';
import { requiredRule } from 'utils/forms/validationRules';
import { thousandFormatter } from 'utils/forms/formatter';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import dayjs from 'dayjs';

function RRPForm({ isActive, onClose, onSuccess, initialValues }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  useEffect(() => {
    if (initialValues) {
      const {
        begin_date,
        end_date,
        monthly_calculation_indicator,
        minimal_celery,
        pit_invalid,
      } = initialValues;

      form.setFieldsValue({
        begin_date: dayjs(begin_date),
        end_date: dayjs(end_date),
        monthly_calculation_indicator,
        minimal_celery,
        pit_invalid,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const compareEndDate = () => {
    form.setFieldsValue({ end_date: null });
  };

  const compareBeginDate = (currentDate) =>
    currentDate && currentDate < form.getFieldValue('begin_date');

  //   TODO: make date by this format YYYY-MM-DD backend help
  const handleSubmit = (values) => {
    const payload = {
      ...values,
      id: initialValues?.id,
      begin_date: values.begin_date.format('YYYY-MM-DD'),
      end_date: values.end_date.format('YYYY-MM-DD'),
    };

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await RRPService.createRRP(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await RRPService.editRRP(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onClose();
    onSuccess();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование РРП' : 'Создание РРП'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item
          name="monthly_calculation_indicator"
          label="МРП"
          rules={[requiredRule]}
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Введите МРП"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="minimal_celery" label="МЗП" rules={[requiredRule]}>
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Введите МЗП"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="pit_invalid"
          label="Вычет по ИПН Инвалиды"
          rules={[requiredRule]}
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Введите вычет по ИПН Инвалиды"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="begin_date" label="Дата начала" rules={[requiredRule]}>
          <DatePicker
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
            onChange={compareEndDate}
          />
        </Form.Item>

        <Form.Item
          name="end_date"
          label="Дата окончание"
          rules={[requiredRule]}
        >
          <DatePicker
            disabledDate={compareBeginDate}
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { RRPForm };
